import { useCallback, useEffect, useRef, useState } from 'react';
import type { Tables } from '@repo/supabase-client';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-llm-runs`;

export type LlmRun = Tables<'llm_stress_test_runs'>;
export type LlmResult = Tables<'llm_stress_test_results'>;

type Status = 'idle' | 'loading' | 'success' | 'error';

export interface RunDetail {
  run: LlmRun;
  results: LlmResult[];
}

interface UseAdminLlmRunsResult {
  status: Status;
  runs: LlmRun[];
  error: string | null;
  lastFetchedAt: Date | null;
  refetch: () => void;
  fetchRunDetail: (runId: string) => Promise<RunDetail | null>;
}

/**
 * Fetches LLM stress-test run data from the password-gated admin-llm-runs
 * edge function. Mirrors useAdminMetrics — auto-refreshes every 60 seconds
 * while visible. fetchRunDetail() expands a single row inline.
 */
export function useAdminLlmRuns(password: string): UseAdminLlmRunsResult {
  const [status, setStatus] = useState<Status>('idle');
  const [runs, setRuns] = useState<LlmRun[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json: { runs: LlmRun[] } = await res.json();
      setRuns(json.runs);
      setLastFetchedAt(new Date());
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  const fetchRunDetail = useCallback(
    async (runId: string): Promise<RunDetail | null> => {
      try {
        const url = new URL(EDGE_FN_URL);
        url.searchParams.set('run_id', runId);
        const res = await fetch(url.toString(), {
          method: 'GET',
          headers: { 'X-Admin-Password': password },
        });
        if (!res.ok) {
          const body = await res
            .json()
            .catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        const json: RunDetail = await res.json();
        return json;
      } catch {
        return null;
      }
    },
    [password],
  );

  useEffect(() => {
    fetchOnce();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, 60_000);
    return () => clearInterval(interval);
  }, [fetchOnce]);

  return {
    status,
    runs,
    error,
    lastFetchedAt,
    refetch: fetchOnce,
    fetchRunDetail,
  };
}
